// src/pages/memberCreate/CreateMemberPending.jsx
import { useMemo } from "react";
import { useAuth } from "@/app/auth-context-state";
import { motion as Motion } from "framer-motion";
import cls from "./CreateMemberPending.module.scss";
import i18n from "@/i18n";

import mrImg from "@/assets/UI/memberCreate/q22Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q22Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q22MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q22MrsM.webp";

const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

export default function CreateMemberPending() {
  const { user } = useAuth();

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const queueLine = t("onboarding.pending.queueLine");
  const queueHint = t("onboarding.pending.queueHint");

  const waitingTitle = t("onboarding.pending.waitingTitle");
  const waitingPoints = [
    t("onboarding.pending.waitingPoint1"),
    t("onboarding.pending.waitingPoint2"),
    t("onboarding.pending.waitingPoint3"),
  ];

  const timezoneTitle = t("onboarding.pending.timezoneTitle");
  const timezoneBody = t("onboarding.pending.timezoneBody");

  const encouragement = t("onboarding.pending.encouragement");
  const notifyLine = t("onboarding.pending.notifyLine");
  const pendingHighlight = t("onboarding.pending.pendingHighlight");

  const footerNote = t("onboarding.pending.footerNote");
  const footerEmail = t("onboarding.pending.footerEmail");
  const footerBottom = t("onboarding.pending.footerBottom");

  const rawCitizenshipType = String(user?.citizenshipType || "").toLowerCase();

  const normalizedCitizenshipType = rawCitizenshipType.includes("lumora")
    ? "lumora"
    : rawCitizenshipType.includes("nexus")
      ? "nexus"
      : "explore";

  const citizenshipPendingContent = {
    badge: t(
      `onboarding.pending.citizenship.${normalizedCitizenshipType}.badge`,
    ),
    title: t(
      `onboarding.pending.citizenship.${normalizedCitizenshipType}.title`,
    ),
    subtitle: t(
      `onboarding.pending.citizenship.${normalizedCitizenshipType}.subtitle`,
    ),
    points: [
      t(`onboarding.pending.citizenship.${normalizedCitizenshipType}.point1`),
      t(`onboarding.pending.citizenship.${normalizedCitizenshipType}.point2`),
      t(`onboarding.pending.citizenship.${normalizedCitizenshipType}.point3`),
      t(`onboarding.pending.citizenship.${normalizedCitizenshipType}.point4`),
    ],
  };

  const cards = [
    {
      title: t("onboarding.pending.card1Title"),
      body: t("onboarding.pending.card1Body"),
    },
    {
      title: t("onboarding.pending.card2Title"),
      body: t("onboarding.pending.card2Body"),
    },
    {
      title: t("onboarding.pending.card3Title"),
      body: t("onboarding.pending.card3Body"),
    },
    {
      title: t("onboarding.pending.card4Title"),
      body: t("onboarding.pending.card4Body"),
    },
    {
      title: t("onboarding.pending.card5Title"),
      body: t("onboarding.pending.card5Body"),
    },
    {
      title: t("onboarding.pending.card6Title"),
      body: t("onboarding.pending.card6Body"),
    },
    {
      title: t("onboarding.pending.card7Title"),
      body: t("onboarding.pending.card7Body"),
    },
  ];

  return (
    <main className={`${cls.page} ${cls[`tier_${normalizedCitizenshipType}`]}`}>
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          {/* <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>
              <div className={cls.formSub}>{introHeader}</div>
              <div className={cls.formSub}>{introBody1}</div>
              <div className={cls.formSub}>{introBody2}</div>
              <div className={cls.formSub}>{reviewBody}</div>
            </div>
          </div> */}

          <div className={cls.form}>
            <div className={cls.readNote}>
              <strong>{queueLine}</strong>
              <p>{queueHint}</p>
            </div>

            <section className={cls.citizenshipSpotlight}>
              <div className={cls.tierBadge}>
                {citizenshipPendingContent.badge}
              </div>
              <h2>{citizenshipPendingContent.title}</h2>
              <p>{citizenshipPendingContent.subtitle}</p>

              <ul>
                {citizenshipPendingContent.points.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            </section>

            <section className={cls.infoRow}>
              <div className={cls.infoBlock}>
                <h2 className={cls.infoTitle}>{waitingTitle}</h2>
                <ul className={cls.waitingList}>
                  {waitingPoints.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </div>

              <div className={cls.infoBlock}>
                <h2 className={cls.infoTitle}>{timezoneTitle}</h2>
                <p className={cls.infoNote}>{timezoneBody}</p>
              </div>
            </section>

            <div className={cls.closingNote}>
              <p className={cls.encouragement}>{encouragement}</p>
              <p className={cls.infoNote}>{notifyLine}</p>
            </div>

            <section className={cls.cardsSection}>
              <div className={cls.cardsGrid}>
                {cards.map((card, idx) => (
                  <article key={idx} className={cls.card}>
                    <h3 className={cls.cardTitle}>{card.title}</h3>
                    <p className={cls.cardBody}>{card.body}</p>
                  </article>
                ))}
              </div>
            </section>

            <div className={cls.pendingHighlight}>{pendingHighlight}</div>

            <footer className={cls.footer}>
              <p className={cls.footerNote}>{footerNote}</p>
              <p className={cls.footerEmail}>
                <a href={`mailto:${footerEmail}`}>{footerEmail}</a>
              </p>
              <p className={cls.footerBottom}>{footerBottom}</p>
            </footer>

            <div className={cls.bottomSpace} />
          </div>
        </div>
      </div>
    </main>
  );
}
